"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
const SCRIM = "bg-[#0b1230]/35 backdrop-blur-[3px] dark:bg-black/55";

/**
 * The popup's frame (spec cova-feed/2026-10-09-001 §6.1).
 *
 * For a broker it is a Radix Dialog portalled to the body: a focus trap,
 * focus returned on close, Escape dismisses (a dismissal of THIS spotlight,
 * the same as the close button). A scrim click does nothing: an announcement
 * that vanishes on a stray click was never seen, and it would still be
 * recorded as seen.
 *
 * `inline` is the composer's Stage: the same frame positioned absolutely
 * inside the compose dialog, with no portal and no trap of its own (the
 * compose dialog already holds focus).
 */
export function SpotlightDialog(t0) {
  const $ = _c(33);
  const {
    label,
    inline: t1,
    reduced,
    onEscape,
    className,
    children
  } = t0;
  const inline = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== inline) {
    t2 = () => {
      if (inline) {
        return;
      }
      const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      return () => {
        window.setTimeout(() => {
          if (previous?.isConnected) {
            previous.focus({
              preventScroll: true
            });
          }
        }, 0);
      };
    };
    t3 = [inline];
    $[0] = inline;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (inline) {
    let t4;
    if ($[3] !== onEscape) {
      t4 = event => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onEscape();
        }
      };
      $[3] = onEscape;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== className) {
      t5 = cn("absolute inset-0 z-30 flex items-center justify-center px-8", className);
      $[5] = className;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div aria-hidden={true} className={cn("absolute inset-0", SCRIM)} />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== children || $[9] !== label || $[10] !== t4 || $[11] !== t5) {
      t7 = <div role="group" aria-label={label} onKeyDown={t4} className={t5}>{t6}{children}</div>;
      $[8] = children;
      $[9] = label;
      $[10] = t4;
      $[11] = t5;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t4;
  if ($[13] !== onEscape) {
    t4 = open => !open && onEscape();
    $[13] = onEscape;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = !reduced && "animate-in fade-in-0 duration-500";
  let t6;
  if ($[15] !== t5) {
    t6 = cn("fixed inset-0 z-60", SCRIM, t5);
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <DialogPrimitive.Overlay className={t6} />;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== className) {
    t8 = cn("fixed inset-0 z-61 flex items-center justify-center p-3 outline-hidden sm:px-8", className);
    $[19] = className;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== label) {
    t9 = <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>;
    $[21] = label;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== children || $[24] !== t8 || $[25] !== t9) {
    t10 = <DialogPrimitive.Content aria-modal="true" aria-describedby={undefined} onPointerDownOutside={_temp} onInteractOutside={_temp2} onOpenAutoFocus={_temp3} className={t8}>{t9}{children}</DialogPrimitive.Content>;
    $[23] = children;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7) {
    t11 = <DialogPrimitive.Portal>{t7}{t10}</DialogPrimitive.Portal>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t4) {
    t12 = <DialogPrimitive.Root open={true} onOpenChange={t4}>{t11}</DialogPrimitive.Root>;
    $[30] = t11;
    $[31] = t4;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
function _temp3(event_2) {
  const target = (event_2.currentTarget as HTMLElement | null)?.querySelector("[data-autofocus]");
  if (target) {
    event_2.preventDefault();
    target.focus({
      preventScroll: true
    });
  }
}
function _temp2(event_1) {
  return event_1.preventDefault();
}
function _temp(event_0) {
  return event_0.preventDefault();
}